import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArtAvailabilityBanner } from './ArtAvailabilityBanner';
import { ArrowLeftRight, ChevronRight, LogOut, Menu, Search, X } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useToast } from '../lib/toast';
import { UiLocaleProvider } from '../lib/i18n';
import { errorMessage } from '../lib/api';
import { APP_HOME_BASENAME, isEmbeddedAdmin } from '../lib/embedded';
import type { Role } from '../lib/types';
import { BrandLockup } from './Brand';
import { useAdminFeatures, useAdminRedesign } from '../lib/adminFeatures';
import { useQuery } from '../lib/query';
import { PULSE_INTERVAL_MS, useSupportAlerts } from '../lib/supportAlerts';
import { ThemeSwitch, useAdminTheme } from '../lib/theme';
import { HelpTip } from './Page';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  /** Shorter label for the Shopify admin app nav (1–2 words). */
  adminLabel?: string;
  /** Extra words the admin page search matches (pt-BR). */
  keywords?: string;
  /** A sub-heading shown before the first item of a run (e.g. "Planejamento"). */
  section?: string;
}

interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

interface ShellText {
  nav: NavGroup[];
  skipLink: string;
  openNav: string;
  closeNav: string;
  navLabel: string;
  homeLabel: string;
  areaLabel?: string;
  contextLabel: string;
  contextName?: string;
  localDemo: string;
  switching: string;
  openOtherView: string;
  switchFailed: string;
  signOut: string;
  signOutFailed: string;
  demoTitle: string;
  demoDetail: string;
}

// The merchant workspace is English. The internal admin is Brazilian Portuguese
// (its pages render inside a pt-BR UiLocaleProvider); login and public pages stay English.
export const SHELL_TEXT: Record<Role, ShellText> = {
  merchant: {
    nav: [
      {
        id: 'sell',
        label: 'Sell',
        items: [
          { to: '/app', label: 'Overview', end: true },
          { to: '/app/styles', label: 'Styles' },
          { to: '/app/mockups', label: 'Mockups' },
          { to: '/app/wizard', label: 'Shopper wizard', adminLabel: 'Wizard' },
          { to: '/app/orders', label: 'Orders' },
          { to: '/app/manual-art', label: 'Customer art' },
        ],
      },
      {
        id: 'measure',
        label: 'Measure',
        items: [
          { to: '/app/analytics', label: 'Analytics' },
          { to: '/app/funnel', label: 'Wizard funnel', adminLabel: 'Funnel' },
          { to: '/app/generations', label: 'Generations' },
          { to: '/app/files', label: 'Files' },
        ],
      },
      {
        id: 'account',
        label: 'Account',
        items: [
          { to: '/app/billing', label: 'Usage & billing', adminLabel: 'Billing' },
          { to: '/app/settings', label: 'Settings' },
          { to: '/app/integrations', label: 'Integrations' },
          { to: '/app/support', label: 'Help & support', adminLabel: 'Support' },
        ],
      },
    ],
    skipLink: 'Skip to content',
    openNav: 'Open navigation',
    closeNav: 'Close navigation',
    navLabel: 'Merchant workspace',
    homeLabel: 'Perzo overview',
    contextLabel: 'Store',
    localDemo: 'Local demo',
    switching: 'Switching…',
    openOtherView: 'Open admin view',
    switchFailed: 'Could not switch demo role',
    signOut: 'Sign out',
    signOutFailed: 'Sign out failed',
    demoTitle: 'Demo mode',
    demoDetail: 'Simulated art. No AI cost, checkout or billing.',
  },
  admin: {
    // Grouped by what Olavo is looking for; every existing /admin route keeps its address.
    nav: [
      { id: 'home', label: 'Início', items: [{ to: '/admin', label: 'Início', end: true, keywords: 'cockpit visão geral painel resumo' }] },
      {
        id: 'business',
        label: 'Negócio',
        items: [
          { to: '/admin/merchants', label: 'Lojistas', keywords: 'lojas clientes merchants' },
          { to: '/admin/early-access', label: 'Acesso antecipado', keywords: 'inscrições early access lista de espera emails site' },
          { to: '/admin/billing', label: 'Cobrança', keywords: 'planos mrr assinaturas ledger faturamento' },
          { to: '/admin/ai-costs', label: 'Custos de IA', keywords: 'openai fal custo provedores' },
          { to: '/admin/benchmarks', label: 'Referências do funil', keywords: 'benchmarks conversão wizard' },
          { to: '/admin/shopper-wait', label: 'Espera do cliente', keywords: 'tempo latência upload fila provedor p95 wizard' },
        ],
      },
      {
        id: 'marketing',
        label: 'Marketing',
        items: [
          { to: '/admin/growth', label: 'Visão geral', end: true, keywords: 'marketing crescimento hub' },
          { to: '/admin/growth/content', label: 'Biblioteca', keywords: 'conteúdo reels carrossel posts instagram imagens vídeos' },
          { to: '/admin/growth/calendar', label: 'Calendário', keywords: 'agenda datas publicações' },
          { to: '/admin/growth/board', label: 'Ideias', keywords: 'claude pautas sugestões quadro editorial' },
          { to: '/admin/growth/performance', label: 'Desempenho', keywords: 'instagram insights alcance engajamento ga4 tráfego site métricas' },
          { to: '/admin/growth/seo', label: 'SEO e IAs', keywords: 'search console google busca palavras-chave consultas chatgpt perplexity geo' },
          { to: '/admin/growth/pages', label: 'Páginas do site', keywords: 'lps landing pages anúncios sitemap indexação noindex guias sazonal legal site' },
          { to: '/admin/growth/writer', label: 'Gerar conteúdo', keywords: 'escrever ideias gerador texto' },
          { to: '/admin/growth/ideas', label: 'Hipóteses', section: 'Planejamento', keywords: 'ideias de crescimento' },
          { to: '/admin/growth/experiments', label: 'Experimentos', section: 'Planejamento', keywords: 'testes cac' },
          { to: '/admin/growth/channels', label: 'Canais', section: 'Planejamento' },
          { to: '/admin/growth/icps', label: 'ICPs', section: 'Planejamento', keywords: 'perfil cliente ideal' },
          { to: '/admin/growth/leads', label: 'Leads', section: 'Planejamento', keywords: 'contatos prospects' },
          { to: '/admin/growth/competitors', label: 'Concorrentes', section: 'Planejamento', keywords: 'customily concorrência preços' },
        ],
      },
      {
        id: 'operations',
        label: 'Operação',
        items: [
          { to: '/admin/alerts', label: 'Alertas', keywords: 'incidentes avisos' },
          { to: '/admin/support', label: 'Suporte', keywords: 'solicitações casos atendimento' },
          { to: '/admin/status', label: 'Status público', keywords: 'incidentes página de status' },
        ],
      },
      {
        id: 'system',
        label: 'Sistema',
        items: [
          { to: '/admin/system', label: 'Saúde do sistema', keywords: 'fila jobs webhooks' },
          { to: '/admin/models', label: 'Modelos', keywords: 'provedores ia' },
          { to: '/admin/audit', label: 'Auditoria', keywords: 'logs registro' },
        ],
      },
    ],
    skipLink: 'Pular para o conteúdo',
    openNav: 'Abrir navegação',
    closeNav: 'Fechar navegação',
    navLabel: 'Admin Perzo',
    homeLabel: 'Início do admin Perzo',
    areaLabel: 'Admin',
    contextLabel: 'Equipe Perzo',
    contextName: 'Todos os lojistas',
    localDemo: 'Demonstração local',
    switching: 'Alternando…',
    openOtherView: 'Abrir visão do lojista',
    switchFailed: 'Não foi possível alternar o perfil de demonstração',
    signOut: 'Sair',
    signOutFailed: 'Não foi possível sair',
    demoTitle: 'Modo demonstração',
    demoDetail: 'As gerações vêm do provedor simulado local: sem gasto com IA, sem checkout da Shopify e sem cobrança real.',
  },
};

export const AFFILIATES_NAV: NavGroup = { id: 'affiliates', label: 'Parcerias', items: [{ to: '/admin/affiliates', label: 'Afiliados', keywords: 'indicação comissão parceiros criadores agências' }] };

export const AUTOPILOT_ADMIN_NAV: NavGroup[] = [
  { id: 'home', label: 'Início', items: [{ to: '/admin', label: 'Início', end: true, keywords: 'cockpit visão geral painel resumo' }] },
  {
    id: 'marketing',
    label: 'Marketing',
    items: [
      { to: '/admin/growth', label: 'Para aprovar', end: true, keywords: 'marketing caixa inbox revisão conteúdo' },
      { to: '/admin/growth/calendar', label: 'Calendário', keywords: 'agenda datas publicações anúncios' },
      { to: '/admin/growth/content', label: 'Biblioteca', keywords: 'conteúdo reels carrossel posts instagram imagens vídeos arquivados' },
      { to: '/admin/growth/performance', label: 'Resultados', keywords: 'desempenho instagram insights tráfego métricas' },
      { to: '/admin/growth/seo', label: 'SEO e IAs', keywords: 'search console google busca palavras-chave consultas chatgpt perplexity geo' },
      { to: '/admin/growth/pages', label: 'Páginas do site', keywords: 'lps landing pages anúncios sitemap indexação noindex guias sazonal legal site' },
    ],
  },
  {
    id: 'merchants',
    label: 'Lojistas',
    items: [
      { to: '/admin/merchants', label: 'Lojistas', keywords: 'lojas clientes merchants' },
      { to: '/admin/early-access', label: 'Acesso antecipado', keywords: 'inscrições early access lista de espera emails site' },
    ],
  },
  { id: 'billing', label: 'Cobrança', items: [{ to: '/admin/billing', label: 'Cobrança', keywords: 'planos mrr assinaturas faturamento' }] },
  { id: 'support', label: 'Suporte', items: [{ to: '/admin/support', label: 'Suporte', keywords: 'solicitações casos atendimento' }] },
  {
    id: 'advanced',
    label: 'Avançado',
    items: [
      { to: '/admin/growth/settings', label: 'Piloto automático', keywords: 'configuração objetivos cadência teto pausa integração' },
      { to: '/admin/growth/materials', label: 'Materiais', keywords: 'biblioteca peças fotos artes capturas upload direitos licença' },
      { to: '/admin/growth/board', label: 'Histórico de ideias', keywords: 'claude pautas sugestões quadro editorial' },
      { to: '/admin/growth/writer', label: 'Editor manual', keywords: 'gerar conteúdo escrever texto' },
      { to: '/admin/growth/advanced', label: 'Pesquisa e experimentos', keywords: 'hipóteses canais icps leads concorrentes' },
      { to: '/admin/ai-costs', label: 'Custos e sistema', keywords: 'openai fal custo provedores saúde fila jobs alertas modelos' },
      { to: '/admin/audit', label: 'Auditoria', keywords: 'logs registro' },
    ],
  },
];

/** The admin sidebar before the redesign (release flag off): same routes, previous grouping. */
export const LEGACY_ADMIN_NAV: NavGroup[] = [
  {
    id: 'platform',
    label: 'Plataforma',
    items: [
      { to: '/admin', label: 'Visão geral', end: true },
      { to: '/admin/merchants', label: 'Lojistas' },
      { to: '/admin/early-access', label: 'Acesso antecipado' },
      { to: '/admin/billing', label: 'Cobrança' },
      { to: '/admin/ai-costs', label: 'Custos de IA' },
      { to: '/admin/benchmarks', label: 'Referências do funil' },
      { to: '/admin/shopper-wait', label: 'Espera do cliente' },
      { to: '/admin/models', label: 'Modelos' },
    ],
  },
  {
    id: 'operations',
    label: 'Operações',
    items: [
      { to: '/admin/system', label: 'Saúde do sistema' },
      { to: '/admin/alerts', label: 'Alertas' },
      { to: '/admin/status', label: 'Status público' },
      { to: '/admin/support', label: 'Suporte' },
      { to: '/admin/audit', label: 'Auditoria' },
    ],
  },
  { id: 'growth', label: 'Crescimento', items: [{ to: '/admin/growth', label: 'Marketing' }] },
];

/** Unread support messages, per area: Perzo replies for the store, store messages for the team. */
export const SUPPORT_UNREAD_PATH: Record<Role, string> = { merchant: '/api/feedback/unread', admin: '/api/admin/requests/unread' };
const SUPPORT_ROUTE: Record<Role, string> = { merchant: '/app/support', admin: '/admin/support' };

/**
 * Nav badges keyed by route: the unread support count, checked every minute while the tab is visible.
 * Admin only: `overdue` holds the conversations past the 1-business-day reply promise.
 */
function useNavBadges(area: Role, enabled = true): { badges: Record<string, number>; overdue: Record<string, number> } {
  const unread = useQuery<{ unread: number; overdue?: number }>(enabled ? SUPPORT_UNREAD_PATH[area] : null, { refetchInterval: area === 'admin' ? PULSE_INTERVAL_MS : 60_000 });
  const n = unread.data?.unread ?? 0;
  const late = area === 'admin' ? (unread.data?.overdue ?? 0) : 0;
  return { badges: n > 0 ? { [SUPPORT_ROUTE[area]]: n } : {}, overdue: late > 0 ? { [SUPPORT_ROUTE[area]]: late } : {} };
}

/**
 * The count after a nav label ("9+" past nine); the label for screen readers says what it counts. Overdue
 * conversations (admin) take over in red: they need an answer even when already read.
 */
export function NavBadge({ count, area, overdue = 0 }: { count: number | undefined; area: Role; overdue?: number }) {
  if (overdue > 0 && area === 'admin') {
    const label = `${overdue} ${overdue === 1 ? 'conversa atrasada' : 'conversas atrasadas'} (mais de 1 dia útil sem resposta)${count ? ` · ${count} não ${count === 1 ? 'lida' : 'lidas'}` : ''}`;
    return (
      <span className="nav-badge nav-badge--danger" aria-label={label} title={label}>
        {overdue > 9 ? '9+' : overdue}
      </span>
    );
  }
  if (!count) return null;
  const label = area === 'admin' ? `${count} ${count === 1 ? 'mensagem não lida' : 'mensagens não lidas'}` : `${count} unread ${count === 1 ? 'reply' : 'replies'}`;
  return (
    <span className="nav-badge" aria-label={label} title={label}>
      {count > 9 ? '9+' : count}
    </span>
  );
}

export function AppShell({ area }: { area: Role }) {
  // Screen readers follow the admin's language while it is mounted.
  useEffect(() => {
    if (area !== 'admin') return;
    const root = document.documentElement;
    const previous = root.lang;
    root.lang = 'pt-BR';
    return () => {
      root.lang = previous;
    };
  }, [area]);

  return (
    <UiLocaleProvider locale={area === 'admin' ? 'pt-BR' : 'en'}>
      <ShellFrame area={area} />
    </UiLocaleProvider>
  );
}

/**
 * Shopify admin (App Home): the admin chrome shows the app's navigation (s-app-nav) and owns the
 * session, so the Perzo sidebar, sign-out and demo role switch are not rendered. Pages are identical.
 */
function EmbeddedShellFrame({ text }: { text: ShellText }) {
  const items = text.nav.flatMap((group) => group.items);
  const { badges } = useNavBadges('merchant');
  return (
    <div className="shell shell--embedded">
      <s-app-nav>
        {items.map((item) => (
          <s-link key={item.to} href={APP_HOME_BASENAME + item.to} rel={item.end ? 'home' : undefined}>
            {item.adminLabel ?? item.label}
            {badges[item.to] ? ` (${badges[item.to]})` : ''}
          </s-link>
        ))}
      </s-app-nav>
      <a href="#main" className="skip-link">
        {text.skipLink}
      </a>
      <div className="main">
        <main id="main" className="main__inner" tabIndex={-1}>
          <ArtAvailabilityBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function ShellFrame({ area }: { area: Role }) {
  if (area === 'merchant' && isEmbeddedAdmin()) return <EmbeddedShellFrame text={SHELL_TEXT.merchant} />;
  return <StandaloneShellFrame area={area} />;
}

function StandaloneShellFrame({ area }: { area: Role }) {
  const { me, demoMode, logout, devLogin, transitionPending } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const text = SHELL_TEXT[area];
  const redesign = useAdminRedesign(area === 'admin');
  const features = useAdminFeatures(area === 'admin');
  const baseGroups = area === 'admin' && redesign === false ? LEGACY_ADMIN_NAV : area === 'admin' && features.data?.marketingInbox === true ? AUTOPILOT_ADMIN_NAV : text.nav;
  // The affiliate program (PERZO_AFFILIATES): its page only when the server advertises it.
  const groups = area === 'admin' && features.data?.affiliates === true ? [...baseGroups, AFFILIATES_NAV] : baseGroups;
  // Dark theme (phase 3): admin redesign only; data-theme lives on <html> while this shell is mounted.
  const [themePref, setThemePref] = useAdminTheme(area === 'admin' && redesign === true);
  const { badges, overdue } = useNavBadges(area, Boolean(me));
  // New store messages chime (and notify, when allowed) while the internal admin is open.
  useSupportAlerts(area === 'admin' && Boolean(me), () => navigate('/admin/support'));

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const otherRole: Role = area === 'admin' ? 'merchant' : 'admin';

  const switchRole = async () => {
    setSwitching(true);
    try {
      const result = await devLogin(otherRole);
      if (result) navigate(result.user.role === 'admin' ? '/admin' : '/app');
    } catch (err) {
      toast.error(text.switchFailed, errorMessage(err));
    } finally {
      setSwitching(false);
    }
  };

  const signOut = async () => {
    try {
      await logout();
    } catch (err) {
      toast.error(text.signOutFailed, errorMessage(err));
    }
    navigate('/login');
  };

  return (
    <div className={area === 'admin' ? `shell shell--admin${redesign ? ' shell--redesign' : ''}` : 'shell'} inert={transitionPending} aria-busy={transitionPending}>
      <a href="#main" className="skip-link">
        {text.skipLink}
      </a>
      <div className="topbar">
        <button type="button" className="topbar__menu" aria-label={text.openNav} aria-expanded={open} aria-controls="sidenav" onClick={() => setOpen(true)}>
          <Menu size={18} aria-hidden="true" />
        </button>
        <BrandLockup subtitle={text.areaLabel} />
      </div>
      <button type="button" className="drawer-backdrop" data-open={open} aria-label={text.closeNav} tabIndex={-1} onClick={() => setOpen(false)} />

      <nav id="sidenav" className="sidenav" data-open={open} aria-label={text.navLabel}>
        <div className="sidenav__head">
          <NavLink to={area === 'admin' ? '/admin' : '/app'} className="sidenav__brand" end aria-label={text.homeLabel}>
            <BrandLockup subtitle={text.areaLabel} />
          </NavLink>
          {open ? (
            <button type="button" className="icon-button icon-button--quiet" aria-label={text.closeNav} onClick={() => setOpen(false)}>
              <X size={16} aria-hidden="true" />
            </button>
          ) : null}
        </div>

        {area === 'admin' ? (
          <div className="sidenav__context">
            <span className="sidenav__context-label">{text.contextLabel}</span>
            <span className="sidenav__context-name">{text.contextName}</span>
          </div>
        ) : me?.merchant ? (
          <div className="sidenav__context">
            <span className="sidenav__context-label">{text.contextLabel}</span>
            <span className="sidenav__context-name" title={me.merchant.name}>
              {me.merchant.name}
            </span>
          </div>
        ) : null}

        {area === 'admin' && redesign === null ? null : area === 'admin' && redesign ? (
          <AdminNav groups={groups} badges={badges} overdue={overdue} />
        ) : (
          groups.map((group) => (
            <div className="sidenav__section" key={group.id} role="group" aria-labelledby={`nav-${group.id}`}>
              <div className="sidenav__section-label" id={`nav-${group.id}`}>
                {group.label}
              </div>
              {group.items.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} className="navlink">
                  <span className="navlink__dot" aria-hidden="true" />
                  {item.label}
                  <NavBadge count={badges[item.to]} area={area} overdue={overdue[item.to]} />
                </NavLink>
              ))}
            </div>
          ))
        )}

        <div className="sidenav__footer">
          {area === 'admin' && redesign ? <ThemeSwitch pref={themePref} onChange={setThemePref} /> : null}
          {demoMode ? (
            <div className="nav-demo">
              <span className="nav-demo__label">{text.localDemo}</span>
              <button type="button" className="navbutton" onClick={switchRole} disabled={transitionPending}>
                <ArrowLeftRight size={14} aria-hidden="true" />
                {switching ? text.switching : text.openOtherView}
              </button>
            </div>
          ) : null}
          <div className="sidenav__user">
            <strong>{me?.user.name}</strong>
            <span>{me?.user.email}</span>
          </div>
          <button type="button" className="navbutton" onClick={signOut} disabled={transitionPending}>
            <LogOut size={14} aria-hidden="true" />
            {text.signOut}
          </button>
        </div>
      </nav>

      <div className="main">
        {demoMode ? <DemoBanner text={text} /> : null}
        <main id="main" className="main__inner" tabIndex={-1}>
          {area === 'merchant' ? <ArtAvailabilityBanner /> : null}
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const COLLAPSED_KEY = 'perzo.admin.nav.collapsed';

function readCollapsed(defaultAdvanced = false): string[] {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : defaultAdvanced ? ['advanced'] : [];
    return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

const fold = (v: string) => v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** Whether a nav item is the current page (same rule as NavLink: `end` means exact). */
export function navItemActive(item: NavItem, pathname: string): boolean {
  return item.end ? pathname === item.to || pathname === `${item.to}/` : pathname === item.to || pathname.startsWith(`${item.to}/`);
}

/** Admin pages matching a search (label, group or keywords; accents and case ignored). */
export function searchNav(groups: NavGroup[], query: string): Array<NavItem & { group: string }> {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return groups.flatMap((g) =>
    g.items
      .filter((i) => {
        const hay = fold(`${i.label} ${g.label} ${i.section ?? ''} ${i.keywords ?? ''}`);
        return words.every((w) => hay.includes(w));
      })
      .map((i) => ({ ...i, group: g.label })),
  );
}

/**
 * Admin sidebar: collapsible groups, a page search ("/" focuses it; Enter opens the first match) and
 * Portuguese labels. Every group can be collapsed, including the one holding the current page; the
 * choice is remembered per group. Navigating INTO a collapsed group opens it; while collapsed, the
 * header of the group holding the current page is highlighted so the page stays discoverable.
 */
export function AdminNav({ groups, badges = {}, overdue = {} }: { groups: NavGroup[]; badges?: Record<string, number>; overdue?: Record<string, number> }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState<string[]>(() => (typeof window === 'undefined' ? [] : readCollapsed(groups.some((group) => group.id === 'advanced'))));
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const matches = searchNav(groups, query);
  const currentGroup = groups.find((g) => g.items.some((i) => navItemActive(i, location.pathname)))?.id ?? null;
  const previousGroup = useRef(currentGroup);

  const persist = (next: string[]) => {
    try {
      window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next));
    } catch {
      /* per-viewer convenience only */
    }
  };

  // Moving to a page of another group opens that group; staying inside it keeps the user's choice.
  useEffect(() => {
    if (currentGroup === previousGroup.current) return;
    previousGroup.current = currentGroup;
    if (!currentGroup) return;
    setCollapsed((cur) => {
      if (!cur.includes(currentGroup)) return cur;
      const next = cur.filter((x) => x !== currentGroup);
      persist(next);
      return next;
    });
  }, [currentGroup]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return;
      event.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggle = (id: string) => {
    setCollapsed((cur) => {
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      persist(next);
      return next;
    });
  };

  return (
    <>
      <div className="navsearch" role="search">
        <Search size={14} aria-hidden="true" className="navsearch__icon" />
        <input
          ref={inputRef}
          type="search"
          className="navsearch__input"
          placeholder="Buscar página"
          aria-label="Buscar página do admin"
          aria-describedby="navsearch-hint"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && matches[0]) {
              navigate(matches[0].to);
              setQuery('');
            } else if (e.key === 'Escape') setQuery('');
          }}
        />
        <kbd className="navsearch__kbd" aria-hidden="true">
          /
        </kbd>
        <span id="navsearch-hint" className="visually-hidden">
          Digite para filtrar; Enter abre o primeiro resultado.
        </span>
      </div>
      {query.trim() ? (
        <div className="sidenav__section" role="group" aria-label="Resultados da busca">
          {matches.length === 0 ? <p className="navsearch__empty">Nenhuma página com “{query}”.</p> : null}
          {matches.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className="navlink" onClick={() => setQuery('')}>
              <span className="navlink__dot" aria-hidden="true" />
              <span>
                {item.label}
                <span className="navlink__group">{item.group}</span>
              </span>
            </NavLink>
          ))}
        </div>
      ) : (
        groups.map((group) => {
          const holdsCurrent = group.id === currentGroup;
          const open = !collapsed.includes(group.id);
          const markCurrent = holdsCurrent && !open;
          if (group.items.length === 1) {
            const item = group.items[0]!;
            return (
              <div className="sidenav__section sidenav__section--single" key={group.id}>
                <NavLink to={item.to} end={item.end} className="navlink navlink--top">
                  <span className="navlink__dot" aria-hidden="true" />
                  {item.label}
                  <NavBadge count={badges[item.to]} area="admin" overdue={overdue[item.to]} />
                </NavLink>
              </div>
            );
          }
          return (
            <div className="sidenav__section" key={group.id}>
              <button
                type="button"
                className="sidenav__group-toggle"
                aria-expanded={open}
                aria-controls={`navgroup-${group.id}`}
                onClick={() => toggle(group.id)}
                data-current={markCurrent ? 'true' : undefined}
                title={markCurrent ? 'A página atual está neste grupo' : undefined}
              >
                <ChevronRight size={14} aria-hidden="true" className="sidenav__chevron" />
                {group.label}
                {markCurrent ? (
                  <>
                    <span className="sidenav__group-dot" aria-hidden="true" />
                    <span className="visually-hidden"> (contém a página atual)</span>
                  </>
                ) : null}
              </button>
              <div id={`navgroup-${group.id}`} role="group" aria-label={group.label} hidden={!open}>
                {group.items.map((item, idx) => (
                  <div key={item.to}>
                    {item.section && item.section !== group.items[idx - 1]?.section ? <div className="sidenav__sub">{item.section}</div> : null}
                    <NavLink to={item.to} end={item.end} className={item.section ? 'navlink navlink--sub' : 'navlink'}>
                      <span className="navlink__dot" aria-hidden="true" />
                      {item.label}
                      <NavBadge count={badges[item.to]} area="admin" overdue={overdue[item.to]} />
                    </NavLink>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </>
  );
}

function DemoBanner({ text }: { text: ShellText }) {
  const { me } = useAuth();
  const detail = me?.demo && typeof me.demo === 'object' && me.demo.description ? me.demo.description : text.demoDetail;
  return (
    <div className="demo-banner" role="note">
      <span className="badge badge--info">{text.demoTitle}</span>
      <HelpTip topic={text.demoTitle} help={detail} />
    </div>
  );
}
